import { Directive, ElementRef, OnDestroy, OnInit, Optional, Renderer2 } from '@angular/core';
import { AbstractControl, NgControl } from '@angular/forms';
import { merge, Subscription } from 'rxjs';

@Directive({
  selector: '[error]',
})
export class ErrorDirective implements OnInit, OnDestroy {
  private sub!: Subscription;
  private errorDiv!: HTMLElement;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Optional() private controlDir: NgControl
  ) {}

  ngOnInit() {
    if (!this.controlDir) return;

    const control = this.controlDir.control;
    if (!control) return;

    this.createErrorDiv();

    // Subscribe to valueChanges and statusChanges to update UI and errors
    this.sub = merge(control.statusChanges!, control.valueChanges!).subscribe(() => {
      this.updateUI(control);
    });

    this.updateUI(control);
  }

  private createErrorDiv(): void {
    this.errorDiv = this.renderer.createElement('div');
    this.renderer.addClass(this.errorDiv, 'error');

    const parent = this.renderer.parentNode(this.el.nativeElement);
    this.renderer.appendChild(parent, this.errorDiv);
  }

  private updateUI(control: AbstractControl): void {
    const errors = control.errors;
    const isTouchedOrDirty = control.touched || control.dirty;

    let errorMessage = '';

    if (errors) {
      if (errors['required']) {
        errorMessage = 'This field is required.';
      } else if (errors['email']) {
        errorMessage = 'Please enter a valid email address.';
      } else if (errors['apiError']) {
        errorMessage = errors['apiError'];
      }
    }

    const showError = errorMessage && isTouchedOrDirty;

    if (showError) {
      // Show the destructive border (.input.err)
      this.renderer.addClass(this.el.nativeElement, 'err');
      this.renderer.setAttribute(this.el.nativeElement, 'aria-invalid', 'true');

      // Show error message text
      this.renderer.setProperty(this.errorDiv, 'textContent', errorMessage);
      this.renderer.setStyle(this.errorDiv, 'display', 'block');
    } else {
      // Remove error styles
      this.renderer.removeClass(this.el.nativeElement, 'err');
      this.renderer.removeAttribute(this.el.nativeElement, 'aria-invalid');

      // Hide error message
      this.renderer.setStyle(this.errorDiv, 'display', 'none');
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.errorDiv) {
      this.renderer.removeChild(this.renderer.parentNode(this.el.nativeElement), this.errorDiv);
    }
  }
}
